import { CSSInterpolation } from '@emotion/css/create-instance'
import { useSelect, type UseSelectStateChange } from 'downshift'
import React from 'react'
import Measure, { type ContentRect } from 'react-measure'

import { joinOptionalStringsToSpaceSeparatedString } from '../../../util/joinOptionalStringsToSpaceSeparatedString'
import { CssVars, NeutralColorToken, SemanticColorToken } from '../../themes'
import { AnchoredOverlay } from '../AnchoredOverlay'
import {
  isMenuOptionSelectable,
  MenuOption,
  MenuOptionsList,
  type MenuOptionsListType,
  type MenuOptionVariantExcludeTitle,
  type MenuOptionVariantSelectable,
  useFlattenedAndFilteredMenuOptions,
} from '../MenuOption'
import { Type } from '../Type'
import { useUniqueId } from '../useUniqueId'
import { ErrorMessageManager } from './ErrorMessageManager'

const LABEL_COLOR_FOR_STATE = {
  default: undefined,
  disabled: NeutralColorToken.Fg2,
  error: SemanticColorToken.Error,
  locked: undefined,
}

export type Props<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = {
  /**
   * The space-separated list of element IDs that describe the component.
   * Can be used to associate this field with error messages shown outside this
   * component.
   */
  'aria-describedby'?: string

  /**
   * A human readable label for the component.
   */
  'aria-label'?: string

  /**
   * The space-separated list of element IDs that provides the label for the
   * component.
   */
  'aria-labelledby'?: string

  /**
   * A testid attribute attached to the outermost `<div>` of this component.
   */
  'data-testid'?: string

  children: (
    // HACK(yan): downshift types return from `getToggleButtonProps` as any
    toggleProps: any,
    isMenuOpen: boolean,
    selectedOption: TOption | null
  ) => React.ReactNode

  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string

  /**
   * A label to show above the input. If you do not provide a value here, please make sure to
   * provide your own `<label>` element for the input for accessibility reasons.
   */
  label?: string

  /**
   * Optionally set the number of visible options that the menu should be limited to. Defaults to 5.
   */
  maxVisibleOptions?: number

  /**
   * Optionally set the positioning of the dropdown menu.
   */
  menuAlign?: 'start' | 'end'

  /**
   * Optionally set width of the menu.
   */
  menuWidth?: number

  /**
   * Callback that will be fired whenever the selected option changes.
   *
   * NOTE: button variant options when selected will not cause this to fire,
   * instead, those options have individual callbacks embedded in the object.
   */
  onChange: (item: TOption) => void

  /**
   * Optional callback function that will be called when the menu closes.
   */
  onMenuClose?: () => void

  /**
   * Optional callback function that will be called when the menu opens.
   */
  onMenuOpen?: () => void

  /**
   * An ordered array of options to be displayed in the dropdown menu.
   *
   * See `MenuOption` component and type definitions for details
   */
  options: MenuOptionsListType<TOption>

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation

  /**
   * Placeholder text to show when no option is selected. If not specified,
   * nothing will be displayed.
   */
  placeholder?: string

  /**
   * Whether to disable the select menu, disable it in a locked state or mark it as having an error.
   */
  state?: 'default' | 'disabled' | 'error' | 'locked'

  /**
   * A React ref to attach to the actual child trigger element, e.g. for triggering focus or blur on
   * the element.
   */
  toggleButtonRef?: React.MutableRefObject<HTMLButtonElement | null>

  /**
   * The unique value of the currently selected option.
   *
   * `null` being no option selected.
   */
  value: TOption['value'] | null
}

export function ListBox<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
>({
  'aria-describedby': externalAriaDescribedBy,
  'aria-label': externalAriaLabel,
  'aria-labelledby': externalAriaLabelledBy,
  children,
  'data-testid': testId,
  errorMessage,
  label = '',
  maxVisibleOptions,
  menuAlign = 'start',
  menuWidth,
  onChange,
  onMenuClose,
  onMenuOpen,
  options,
  overrideRootStyles,
  placeholder,
  state,
  toggleButtonRef: externalToggleElementRef,
  value: selectedItemValue,
}: Props<TOption>) {
  const flattenedOptions = useFlattenedAndFilteredMenuOptions<TOption>(options)

  const selectedItem = React.useMemo(
    () =>
      flattenedOptions.find(
        (o): o is TOption =>
          isMenuOptionSelectable(o) && o.value === selectedItemValue
      ) ?? null,
    [flattenedOptions, selectedItemValue]
  )

  const [isMenuOpen, setMenuIsOpen] = React.useState(false)

  const internalToggleElementRef = React.useRef<HTMLButtonElement | null>(null)
  const setToggleElementRefs = React.useCallback(
    (controlElement: HTMLButtonElement | null) => {
      internalToggleElementRef.current = controlElement
      if (externalToggleElementRef) {
        externalToggleElementRef.current = controlElement
      }
    },
    [internalToggleElementRef, externalToggleElementRef]
  )

  // NOTE(jare): We keep track of the width of the toggle element so that we can
  // set the size of the menu, which is rendered in a portal, to match
  const [toggleElementWidth, setToggleElementWidth] = React.useState(0)
  const handleToggleElementResize = React.useCallback((rect: ContentRect) => {
    setToggleElementWidth(rect.bounds?.width ?? 0)
  }, [])

  // NOTE(yan): downshift's built in id system is sequential. when there's
  //   multiple instances of the design system being used on a page, the ids
  //   might collide. so we prefix with an uuid to ensure global uniqueness.
  const selectMenuInstanceId = useUniqueId()
  const menuId = `${selectMenuInstanceId}-options-menu-list`
  const labelId = label ? `${selectMenuInstanceId}-label` : undefined
  const handleGetOptionElementIdentifier = React.useCallback(
    (option: TOption) => {
      return `${selectMenuInstanceId}-options-menu-list-option-${option.value.replace(
        '.',
        '_'
      )}`
    },
    [selectMenuInstanceId]
  )
  const handleGetOptionElementIdentifierByIndex = React.useCallback(
    (index: number) => {
      const option = flattenedOptions[index]
      if (isMenuOptionSelectable(option)) {
        return handleGetOptionElementIdentifier(option)
      }
      return `${selectMenuInstanceId}-options-menu-list-option-${index}`
    },
    [selectMenuInstanceId, flattenedOptions, handleGetOptionElementIdentifier]
  )

  const handleSelectedItemChange = React.useCallback(
    (changes: UseSelectStateChange<MenuOptionVariantExcludeTitle<TOption>>) => {
      if (isMenuOptionSelectable(changes.selectedItem)) {
        onChange(changes.selectedItem)
      } else if (changes.selectedItem?.variant === 'button') {
        changes.selectedItem.onClick()
      }
    },
    [onChange]
  )

  // NOTE(yan):
  //   when SelectMenu is rendered on initial page load, we expect this error:
  //     `downshift: You forgot to call the getMenuProps getter function on
  //      your component / element.`
  //   this is due <LayersProvider> needing one update cycle to create the
  //     container div for <Layer> to render its contents in. the select menu
  //     list element is rendered through <AnchoredOverlay>, which depends on <Layer>.
  //   see the relevant comment in <LayersProvider> for more details.
  const {
    closeMenu,
    getItemProps,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps: getToggleElementProps,
    highlightedIndex,
  } = useSelect({
    getItemId: handleGetOptionElementIdentifierByIndex,
    isOpen: isMenuOpen,
    itemToString: option => (option ? option.label : placeholder ?? ''),
    items: flattenedOptions,
    labelId,
    menuId,
    onIsOpenChange: changes => {
      setMenuIsOpen(changes.isOpen ?? false)
      // call the appropriate callback if provided
      if (onMenuOpen && changes.isOpen) {
        onMenuOpen()
      } else if (onMenuClose && !changes.isOpen) {
        onMenuClose()
      }
    },
    onSelectedItemChange: handleSelectedItemChange,
    selectedItem,
  })

  // NOTE(yan):
  //   we're intentionally setting `aria-labelledby` to undefined on the toggle
  //     element and menu list when there's no aria-labelledby provided, nor any
  //     label string intended to be displayed.
  //   downshift automatically adds in `aria-labelledby="downshift-0-label"` to
  //     the toggle element and menu list elements even when there's no label
  //     being rendered. in some enviroments (Chrome + VoiceOver) having that
  //     aria-labelledby set to reference to a non-existent element in the DOM
  //     breaks the screen reader. so we need to explicitly unset it.
  //   see: https://github.com/downshift-js/downshift/issues/1190
  const ariaLabelledBy = joinOptionalStringsToSpaceSeparatedString(
    externalAriaLabelledBy,
    labelId
  )

  // NOTE(yan):
  //    when the menu blurs, we need to close the menu, EXCEPT these cases:
  //      (1) when it is due to mousedown on the toggle element, or
  //      (2) when it is due to mousedown on one of the option elements
  //    in either cases, the click handler provided by downshift on the toggle
  //      element and option element will close the menu automatically.
  //    we need to use mouseup to unset the mousedown flag for the toggle
  //      element. otherwise, the first click to open the menu will have the
  //      flag stuck in the on state since the menu would not blur yet.
  //    we don't need to manually unset the mousedown flag for the menu item
  //      button because after clicking on an option, the menu will blur, going
  //      through the handleMenuBlur callback, thus unsetting the flag.

  const wasMouseDownOnChildElementBeforeMenuBlur = React.useRef(false)

  const handleMenuBlur = React.useCallback(() => {
    if (!wasMouseDownOnChildElementBeforeMenuBlur.current) {
      closeMenu()
    } else {
      wasMouseDownOnChildElementBeforeMenuBlur.current = false
    }
  }, [closeMenu])

  const handleMenuItemMouseDown = React.useCallback(() => {
    wasMouseDownOnChildElementBeforeMenuBlur.current = true
  }, [])

  const handleToggleElementMouseDown = React.useCallback(() => {
    wasMouseDownOnChildElementBeforeMenuBlur.current = true
  }, [])

  const handleToggleElementMouseUp = React.useCallback(() => {
    wasMouseDownOnChildElementBeforeMenuBlur.current = false
  }, [])

  const { ref: menuRef, ...menuProps } = getMenuProps({
    'aria-labelledby': ariaLabelledBy,
    onBlur: handleMenuBlur,
  })

  const selectedOptionIdentifier =
    selectedItem && handleGetOptionElementIdentifier(selectedItem)
  const shouldInsetAllOptions =
    !!selectedItem ||
    flattenedOptions.some(
      o => (!o.variant || o.variant === 'default') && !!o.icon
    )

  return (
    <ErrorMessageManager
      errorMessage={state === 'error' ? errorMessage : undefined}
    >
      {(errorMessageId, errorMessageElem) => (
        <div css={overrideRootStyles} data-testid={testId}>
          {label && (
            <label
              {...getLabelProps()}
              css={{
                display: 'block',
                marginBottom: CssVars.size.gutterWidthSm,
                width: '100%',
              }}
            >
              <Type.Text color={state && LABEL_COLOR_FOR_STATE[state]} size="S">
                {label}
              </Type.Text>
            </label>
          )}
          <Measure bounds onResize={handleToggleElementResize}>
            {({ measureRef }: { measureRef: React.LegacyRef<HTMLDivElement> }) => {
              /* NOTE(yan):
                  ideally (but not required) menu element (with role="listbox")
                    should be directly after the toggle element that invoked it.
                  however, we have to display the listbox through a portal so
                    that it can avoid being clipped by a parent container and
                    float above all elements on the page
                  hence, we use aria-owns to associate the menu with this div so
                    that assistive tech can detect the menu displayed elsewhere
                    in the DOM as a child of this div, and thus a sibling of the
                    toggle element.
                  also, the toggle element is directly associated with the dropdown via
                    aria-controls.
                 */
              return (
                <div ref={measureRef} aria-owns={menuId}>
                  {children(
                    getToggleElementProps({
                      'aria-controls': menuId,
                      'aria-describedby':
                        joinOptionalStringsToSpaceSeparatedString(
                          externalAriaDescribedBy,
                          errorMessageId
                        ),
                      'aria-label': externalAriaLabel || placeholder,
                      'aria-labelledby': ariaLabelledBy,
                      disabled: state === 'disabled' || state === 'locked',
                      onMouseDown: handleToggleElementMouseDown,
                      onMouseUp: handleToggleElementMouseUp,
                      ref: setToggleElementRefs,
                      type: 'button',
                    }),
                    isMenuOpen,
                    selectedItem
                  )}
                </div>
              )
            }}
          </Measure>
          {errorMessageElem}
          <AnchoredOverlay
            dangerouslyEnforceZIndex={
              CssVars.size._experimentalSelectMenuZIndex
            }
            gap={4}
            isOpen={isMenuOpen}
            isPersistent
            placement={menuAlign === 'start' ? 'bottom-start' : 'bottom-end'}
            targetEl={internalToggleElementRef.current}
          >
            <MenuOptionsList<TOption>
              innerRef={menuRef}
              isMenuOpen={isMenuOpen}
              minWidth={toggleElementWidth}
              optionsList={options}
              selectedOptionIdentifier={selectedOptionIdentifier}
              width={menuWidth}
              {...menuProps}
            >
              {(option, optionIndex) => {
                const optionValue: TOption['value'] | undefined =
                  isMenuOptionSelectable(option) ? option.value : undefined
                const { ref, ...itemProps } = getItemProps({
                  disabled:
                    isMenuOptionSelectable(option) &&
                    option.state === 'disabled',
                  index: optionIndex,
                  item: option,
                  onMouseDown: handleMenuItemMouseDown,
                })
                return (
                  <MenuOption<TOption>
                    // HACK(eric.lovett): If the items in this menu have integer values, then
                    // it's possible for one of them to have the same value as the index of a
                    // button entry, which results in a React key collision. To reduce the risk
                    // of collisions, we add a prefix to the key that is very unlikely to
                    // collide with real usage values.
                    key={optionValue || `--Bento-Web-key-${optionIndex}`}
                    index={optionIndex}
                    innerRef={ref}
                    isHighlighted={optionIndex === highlightedIndex}
                    isInset={shouldInsetAllOptions}
                    isSelected={
                      isMenuOptionSelectable(option) &&
                      option.value === selectedItem?.value
                    }
                    maxVisibleOptions={maxVisibleOptions}
                    option={option}
                    {...itemProps}
                  />
                )
              }}
            </MenuOptionsList>
          </AnchoredOverlay>
        </div>
      )}
    </ErrorMessageManager>
  )
}
